/**
 * DataPreviewTab - Data preview with query controls
 *
 * Allows querying view data with limit/offset controls and displays results
 * in a DataTable with per-column text filtering (debounced server-side).
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Download, Play, Loader2, Search } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import type { ViewFilter, ViewColumn } from '@/types/views';
import { formatExecutionTime } from '@/features/query/utils/formatExecutionTime';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { DataTable, DataTableSortHeader } from '@/components/ui/data-table';
import { useViewData } from '@/hooks/useViews';
import { downloadCsv, type CsvExportColumn } from '@/lib/utils/csv-export';

interface DataPreviewTabProps {
  viewId: string;
  viewColumns?: ViewColumn[];
}

type Row = Record<string, unknown>;

/**
 * Per-column filter input rendered under the sort header. Uses a small
 * `<Input>` with a search icon. Debouncing happens at the page level so
 * keystrokes don't immediately re-fetch.
 */
const ColumnFilterInput: React.FC<{
  value: string;
  onChange: (next: string) => void;
}> = ({ value, onChange }) => (
  <div className="relative">
    <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground pointer-events-none" />
    <Input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Filter…"
      className="h-7 pl-7 text-xs font-normal"
      onClick={(e) => e.stopPropagation()}
    />
  </div>
);

/**
 * Translate column → search-text map into the backend's ViewFilter[]. All
 * column filters use the `contains` operator; this drops AG Grid's number
 * /date operators and compound AND/OR conditions. Power users who need
 * those can be reintroduced later by extending ColumnFilterInput to a
 * popover with operator selection.
 */
function buildFilters(columnFilters: Record<string, string>): ViewFilter[] {
  return Object.entries(columnFilters)
    .filter(([, v]) => v.trim() !== '')
    .map(([column, value]) => ({ column, operator: 'contains', value }));
}

/**
 * Extract a top-level AS alias from a SQL expression, ignoring AS inside
 * parenthesised sub-expressions (e.g. CAST(x AS INTEGER)).
 */
function extractTopLevelAlias(expr: string): string | null {
  let depth = 0;
  let stripped = '';
  for (const ch of expr) {
    if (ch === '(') depth++;
    else if (ch === ')') depth = Math.max(depth - 1, 0);
    else if (depth === 0) stripped += ch;
  }
  const match = stripped.match(/\bAS\s+["']?(\w+)["']?\s*$/i);
  return match ? match[1] : null;
}

/**
 * DataPreviewTab Component
 */
export const DataPreviewTab: React.FC<DataPreviewTabProps> = ({ viewId, viewColumns }) => {
  const [limit, setLimit] = useState(100);
  const [offset, setOffset] = useState(0);
  const [enabled, setEnabled] = useState(false);
  /** Map of column-name → in-progress filter text (pre-debounce). */
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  /** Debounced filters passed to the API. */
  const [filters, setFilters] = useState<ViewFilter[]>([]);
  const [unfilteredTotalRows, setUnfilteredTotalRows] = useState<number | null>(null);

  // Debounce columnFilters → API filters (500ms). When the user is typing
  // in a column filter input, we want a single refetch per pause.
  useEffect(() => {
    const handle = setTimeout(() => {
      setFilters(buildFilters(columnFilters));
      setOffset(0);
    }, 500);
    return () => clearTimeout(handle);
  }, [columnFilters]);

  // Fetch view data. When filters are active, the limit auto-bumps so the
  // server has more rows to filter against (matches the prior AG Grid flow).
  const activeLimit = filters.length > 0 ? Math.max(limit, 1000) : limit;
  const { data, isLoading, error } = useViewData(
    viewId,
    { limit: activeLimit, offset, filters },
    { enabled }
  );

  // Track unfiltered total for status display. Mirrors the previous `data` /
  // `filters.length` pair in state so this can run as a guarded render-time
  // adjustment (react-hooks/set-state-in-effect) instead of a useEffect —
  // same trigger conditions as the effect's old `[data, filters.length]` deps,
  // converging in one extra render.
  const [prevPreviewData, setPrevPreviewData] = useState(data);
  const [prevFiltersCount, setPrevFiltersCount] = useState(filters.length);
  if (data !== prevPreviewData || filters.length !== prevFiltersCount) {
    setPrevPreviewData(data);
    setPrevFiltersCount(filters.length);
    if (data && filters.length === 0) {
      setUnfilteredTotalRows(data.total_rows);
    }
  }

  const handleQuery = () => {
    setEnabled(true);
  };

  const handleColumnFilterChange = useCallback((column: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [column]: value }));
  }, []);

  /**
   * Extract column names from the first row. API returns rows as
   * Record<string, unknown>.
   */
  const columnNames = useMemo<string[]>(() => {
    if (!data?.data || !Array.isArray(data.data) || data.data.length === 0) return [];
    return Object.keys(data.data[0]);
  }, [data]);

  const handleExport = () => {
    if (!data?.data || !Array.isArray(data.data) || data.data.length === 0) return;
    const rows = data.data as Row[];
    const csvColumns: CsvExportColumn<Row>[] = columnNames.map((name) => ({
      header: name,
      accessor: (row) => row[name],
    }));
    downloadCsv(rows, csvColumns, `view_${viewId}_data`);
  };

  /**
   * Map each result column to its source via the viewColumns metadata. Used
   * to group columns under a source-name parent in the table header.
   */
  const sourceByDisplayName = useMemo(() => {
    const map = new Map<string, string>();
    if (!viewColumns || viewColumns.length === 0) return map;
    for (const vc of viewColumns) {
      const source = vc.source_name || 'Base Dataset';
      if (vc.column_alias) {
        map.set(vc.column_alias, source);
      } else if (vc.transform_expression) {
        const alias = extractTopLevelAlias(vc.transform_expression);
        if (alias) map.set(alias, source);
      }
      map.set(vc.source_column, source);
    }
    return map;
  }, [viewColumns]);

  /**
   * Build TanStack column definitions. Each leaf column is the data column
   * with sort + filter input. If viewColumns metadata is available, columns
   * get grouped by their source under a parent ColumnDef.
   */
  const columns = useMemo<ColumnDef<Row, unknown>[]>(() => {
    if (columnNames.length === 0) return [];

    const makeLeaf = (name: string): ColumnDef<Row, unknown> => ({
      id: name,
      accessorKey: name,
      header: ({ column }) => (
        <div className="space-y-1">
          <DataTableSortHeader column={column}>{name}</DataTableSortHeader>
          <ColumnFilterInput
            value={columnFilters[name] ?? ''}
            onChange={(value) => handleColumnFilterChange(name, value)}
          />
        </div>
      ),
      cell: ({ row }) => {
        const value = row.original[name];
        if (value === null || value === undefined) {
          return <span className="text-muted-foreground">null</span>;
        }
        return String(value);
      },
    });

    // No metadata or no matches → flat columns.
    if (sourceByDisplayName.size === 0) {
      return columnNames.map(makeLeaf);
    }

    // Group columns by source, preserving order. Ungrouped columns get an
    // "Other" parent (matches the prior AG Grid grouping).
    const groups = new Map<string, ColumnDef<Row, unknown>[]>();
    const ungrouped: ColumnDef<Row, unknown>[] = [];
    for (const name of columnNames) {
      const source = sourceByDisplayName.get(name);
      if (source) {
        if (!groups.has(source)) groups.set(source, []);
        groups.get(source)!.push(makeLeaf(name));
      } else {
        ungrouped.push(makeLeaf(name));
      }
    }
    if (groups.size === 0) return ungrouped;

    const result: ColumnDef<Row, unknown>[] = [];
    for (const [sourceName, children] of groups) {
      result.push({
        id: `group:${sourceName}`,
        header: sourceName,
        columns: children,
      });
    }
    if (ungrouped.length > 0) {
      result.push({
        id: 'group:other',
        header: 'Other',
        columns: ungrouped,
      });
    }
    return result;
  }, [columnNames, columnFilters, handleColumnFilterChange, sourceByDisplayName]);

  const rowData = useMemo<Row[]>(() => {
    if (!data?.data || !Array.isArray(data.data)) return [];
    return data.data as Row[];
  }, [data]);

  return (
    <div className="space-y-6">
      {/* Query Controls */}
      <Card>
        <CardHeader>
          <CardTitle>Query Controls</CardTitle>
          <CardDescription>Configure query parameters and execute view query</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <Label htmlFor="limit">Limit</Label>
              <Input
                id="limit"
                type="number"
                min="1"
                max="10000"
                value={limit}
                onChange={(e) => setLimit(parseInt(e.target.value) || 100)}
                placeholder="100"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Max rows to return (1-10,000)
                {filters.length > 0 && activeLimit > limit && (
                  <span className="text-info"> • Auto-increased to {activeLimit} for filtering</span>
                )}
              </p>
            </div>
            <div>
              <Label htmlFor="offset">Offset</Label>
              <Input
                id="offset"
                type="number"
                min="0"
                value={offset}
                onChange={(e) => setOffset(parseInt(e.target.value) || 0)}
                placeholder="0"
              />
              <p className="text-xs text-muted-foreground mt-1">Number of rows to skip</p>
            </div>
            <div className="flex items-start pt-[22px]">
              <Button onClick={handleQuery} disabled={isLoading} className="w-full">
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Querying...
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    Query View
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>
            {error.message || 'Failed to query view data'}
          </AlertDescription>
        </Alert>
      )}

      {data && data.data && (
        <>
          {/* Results Info */}
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Showing {rowData.length} rows
              {filters.length > 0 && unfilteredTotalRows != null
                ? ` (${data.total_rows.toLocaleString()} filtered of ${unfilteredTotalRows.toLocaleString()} total)`
                : data.total_rows ? ` of ${data.total_rows.toLocaleString()} total` : ''
              }
              {' • '}
              Execution time: {formatExecutionTime(data.execution_time_ms)}
            </div>
            <Button variant="outline" size="sm" onClick={handleExport}>
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          </div>

          {/* Data Table */}
          <Card>
            <CardContent className="pt-6">
              <DataTable
                data={rowData}
                columns={columns}
                pageSize={50}
                emptyState={
                  filters.length > 0
                    ? 'No rows match the current filters.'
                    : 'No rows.'
                }
              />
            </CardContent>
          </Card>
        </>
      )}

      {!data && !isLoading && !error && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-muted-foreground">
              <Play className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">No Data Loaded</p>
              <p className="text-sm">Click "Query View" to load data preview</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
